import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
    WEALTH_ASSET_BANKABLE_CATEGORY_ID,
    WEALTH_ASSET_INSURANCE_CATEGORY_ID,
    WEALTH_ASSET_OTHER_CATEGORY_ID,
    WEALTH_ASSET_PENSION_CATEGORY_ID,
    WEALTH_ASSET_REAL_ESTATE_CATEGORY_ID,
    WEALTH_ASSET_SHAREHOLDING_CATEGORY_ID,
    WEALTH_EXPENSE_GENERAL_LIVING,
    WEALTH_EXPENSE_INTEREST_BURDEN,
    WEALTH_EXPENSE_OTHER,
    WEALTH_EXPENSE_PREMIUMS,
    WEALTH_EXPENSE_REAL_ESTATE,
    WEALTH_INCOME_EMPLOYMENT,
    WEALTH_INCOME_INSURANCE,
    WEALTH_INCOME_OTHER,
    WEALTH_INCOME_PENSION,
    WEALTH_INCOME_REAL_ESTATE,
    WEALTH_INCOME_SECURITIES,
    WEALTH_INCOME_SHAREHOLDING,
    WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID,
    WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID,
    WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID,
} from '../../../../config/constants';
import useFederatedModule from '../../../../utils/useFederatedModule';
import { MessageModal } from '../../../modal';
import type { AssetModalErrorCategory, ClmBankableAsset, ClmModalActionType, ClmModalWcProps, IAssetModalProps } from './types';

interface IProps<T> extends IAssetModalProps<T> {
    woCategoryId: number;
}

export const ClmGenericEntryModalWc: FunctionComponent<IProps<ClmBankableAsset>> = ({
    woItemUuid,
    partnerType,
    partnerUuid,
    actionType,
    type,
    onClose,
    onRemoveWoItem,
    onError,
    onSubmit,
    woCategoryId,
}) => {
    const { t } = useTranslation();
    const modalRef = useRef<HTMLElement & IAssetModalProps<ClmBankableAsset>>(null);

    const { status } = useFederatedModule(woCategoryId);

    const onModalClose = useCallback(() => {
        // The CLM modal MFE adds an "overflow=hidden" style, which breaks the scrolling in WN. Remove on close
        if (document.body.style.overflow === 'hidden') document.body.style.removeProperty('overflow');
        onClose();
    }, [onClose]);

    useLayoutEffect(() => {
        if (status !== 'done') return;

        const assetModalElement = modalRef.current!;

        assetModalElement.addEventListener('closeAction', onModalClose);
        assetModalElement.addEventListener('removeAssetAction', onRemoveWoItem);
        assetModalElement.addEventListener('submitAction', onSubmit);
        assetModalElement.addEventListener('errorAction', handleErrorActionEvent);

        // add event listeners for Custom Events which are emitted for a callbacks/outputs
        function handleErrorActionEvent(event: Event) {
            console.error(event);
            const { category, message } = (
                event as CustomEvent<{
                    category: AssetModalErrorCategory;
                    message: string;
                }>
            ).detail;
            onError(category, message);
        }

        // custom callbacks
        if (woCategoryId === WEALTH_ASSET_BANKABLE_CATEGORY_ID) {
            assetModalElement.customValidation = (_actionType: ClmModalActionType, asset: ClmBankableAsset) => {
                return asset.type === 'PORTFOLIO' && !asset.strategy
                    ? { valid: false, validationMessage: t('errorClmInvestmentStrategyRequired') }
                    : { valid: true, validationMessage: '' };
            };
        }
    }, [status, onModalClose, onRemoveWoItem, onSubmit, onError, woCategoryId, t]);

    // Properly close the modal on unmount
    useEffect(() => {
        return () => {
            onModalClose();
        };
    }, [onModalClose]);

    const getModalJsx = () => {
        const modalParams: ClmModalWcProps<ClmBankableAsset> = {
            ref: modalRef,
            type,
            style: { all: 'initial', position: 'fixed', zIndex: 9000 },
            woItemUuid,
            partnerType,
            partnerUuid,
            actionType,
            version: '0.0.1',
        };

        switch (woCategoryId) {
            case WEALTH_ASSET_BANKABLE_CATEGORY_ID:
                return <wo-bankable-asset {...modalParams} />;
            case WEALTH_ASSET_PENSION_CATEGORY_ID:
                return <wo-pension-asset {...modalParams} />;
            case WEALTH_ASSET_INSURANCE_CATEGORY_ID:
                return <wo-insurance-asset {...modalParams} />;
            case WEALTH_ASSET_REAL_ESTATE_CATEGORY_ID:
                return <wo-real-estate-asset {...modalParams} />;
            case WEALTH_ASSET_SHAREHOLDING_CATEGORY_ID:
                return <wo-shareholding-asset {...modalParams} />;
            case WEALTH_ASSET_OTHER_CATEGORY_ID:
                return <wo-other-asset {...modalParams} />;
            case WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID:
            case WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID:
            case WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID:
            case WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID:
            case WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID:
            case WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID:
                return <wo-liability-modal {...modalParams} />;
            case WEALTH_EXPENSE_PREMIUMS:
            case WEALTH_EXPENSE_REAL_ESTATE:
            case WEALTH_EXPENSE_GENERAL_LIVING:
            case WEALTH_EXPENSE_INTEREST_BURDEN:
            case WEALTH_EXPENSE_OTHER:
                return <wo-expense-modal {...modalParams} />;
            case WEALTH_INCOME_EMPLOYMENT:
            case WEALTH_INCOME_OTHER:
            case WEALTH_INCOME_SECURITIES:
            case WEALTH_INCOME_REAL_ESTATE:
            case WEALTH_INCOME_PENSION:
            case WEALTH_INCOME_INSURANCE:
            case WEALTH_INCOME_SHAREHOLDING:
                return <wo-income-modal {...modalParams} />;
            default:
                return null;
        }
    };

    return createPortal(
        <div>
            {status === 'loading' ? (
                <ZfLoadingSpinner style={{ margin: 'auto' }} />
            ) : status === 'error' ? (
                <MessageModal messageKey="failureActionGeneric" onClose={onModalClose} />
            ) : (
                getModalJsx()
            )}
        </div>,
        document.body,
    );
};
